import { Link, useLocation } from 'react-router-dom'
import { routes } from '@/app/routes'
import { Brand } from '@/components/shared/Brand'
import { isNavActive, sidebarSectionsFor } from '@/components/navigation/nav-items'
import { setSidebarCollapsed, useSidebarCollapsed } from '@/components/navigation/sidebar-state'
import { shellStrings as s } from '@/components/navigation/strings'
import { useUser } from '@/features/auth/useSession'
import { env } from '@/config/env'
import { cn, mediaUrl } from '@/lib/utils'
import { fullName } from '@/types/models'
import { DsAvatar, DsIconButton, DsIconGlyph, DsIcons, DsTooltip } from '@/ds'

export function Sidebar() {
  const user = useUser()
  const { pathname } = useLocation()
  const collapsedPref = useSidebarCollapsed()
  if (!user) return null
  const sections = sidebarSectionsFor(user.defaultProfile)
  const collapsed = collapsedPref
  return (
    <aside
      className={cn('fixed inset-y-0 left-0 z-20 hidden flex-col border-r bg-card transition-[width] motion-base md:flex', collapsed ? 'w-[4.5rem]' : 'w-64')}
      aria-label={s.main}
    >
      <div className={cn('flex h-16 shrink-0 items-center', collapsed ? 'justify-center gap-1 px-1' : 'justify-between px-5')}>
        {collapsed ? <Brand variant="mark" size="sm" /> : <Brand />}
        <DsIconButton
          icon={collapsed ? DsIcons.chevronRight : DsIcons.leftArrow}
          semanticLabel={collapsed ? s.expand : s.collapse}
          size="sm"
          onClick={() => setSidebarCollapsed(!collapsed)}
          className="hidden lg:inline-flex"
        />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-3">
        {sections.map((section, i) => (
          <div key={section.label ?? `s${i}`} className={cn(i > 0 && 'mt-4')}>
            {section.label &&
              (collapsed ? (
                <div aria-hidden className="mx-2 mb-2 border-t" />
              ) : (
                <div className="px-3 pb-1 text-overline uppercase text-muted-foreground">{section.label}</div>
              ))}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = isNavActive(item, pathname)
                const link = (
                  <Link
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    aria-label={collapsed ? item.label : undefined}
                    className={cn(
                      'flex h-10 items-center gap-3 rounded-lg text-small-strong outline-none transition-colors motion-fast focus-visible:ring-focus',
                      collapsed ? 'justify-center px-0' : 'px-3',
                      active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <DsIconGlyph icon={item.icon} size={18} />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </Link>
                )
                return (
                  <li key={item.to}>
                    {collapsed ? (
                      <DsTooltip content={item.label} side="right">
                        {link}
                      </DsTooltip>
                    ) : (
                      link
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>
      <Link
        to={routes.account}
        className={cn('flex shrink-0 items-center gap-3 border-t outline-none hover:bg-muted focus-visible:ring-focus', collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3')}
        aria-label={collapsed ? fullName(user) : undefined}
      >
        <DsAvatar name={fullName(user)} imageUrl={mediaUrl(user.avatar, env.mediaHost)} size="sm" />
        {!collapsed && (
          <span className="min-w-0">
            <span className="block truncate text-small-strong text-foreground">{fullName(user)}</span>
            <span className="block truncate text-caption text-muted-foreground">{s.signedInAs(user.defaultProfile === 'landlord' ? s.landlord : s.tenant)}</span>
          </span>
        )}
      </Link>
    </aside>
  )
}
